import { createAuth0Client, type Auth0Client } from "@auth0/auth0-spa-js";

const mode = import.meta.env.VITE_AUTH_MODE ?? "dev";
const domain = import.meta.env.VITE_AUTH0_DOMAIN ?? "";
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";
const audience = import.meta.env.VITE_AUTH0_AUDIENCE ?? "";
const defaultTenantId = import.meta.env.VITE_DEFAULT_TENANT_ID ?? "";

export const managedAuthEnabled = mode === "oidc";
export const managedDefaultTenantId = defaultTenantId;

let clientPromise: Promise<Auth0Client> | null = null;

function requireManagedConfig() {
  if (!managedAuthEnabled) return;
  if (!domain || !clientId || !audience) {
    throw new Error("managed dashboard auth configuration is incomplete");
  }
}

function getClient(): Promise<Auth0Client> {
  requireManagedConfig();
  if (!clientPromise) {
    clientPromise = createAuth0Client({
      domain,
      clientId,
      cacheLocation: "memory",
      useRefreshTokens: false,
      authorizationParams: {
        audience,
        redirect_uri: window.location.origin,
      },
    });
  }
  return clientPromise;
}

export async function prepareManagedAuth(): Promise<void> {
  if (!managedAuthEnabled) return;
  const auth = await getClient();
  const params = new URLSearchParams(window.location.search);
  if (params.has("code") && params.has("state")) {
    await auth.handleRedirectCallback();
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.hash,
    );
  }
}

export async function managedIsAuthenticated(): Promise<boolean> {
  if (!managedAuthEnabled) return false;
  return (await getClient()).isAuthenticated();
}

export async function managedLogin(): Promise<void> {
  const auth = await getClient();
  await auth.loginWithRedirect({
    authorizationParams: {
      audience,
      redirect_uri: window.location.origin,
    },
  });
}

export async function managedAccessToken(): Promise<string> {
  const auth = await getClient();
  const token = await auth.getTokenSilently({
    authorizationParams: { audience },
  });
  if (!token) throw new Error("managed access token unavailable");
  return token;
}

export async function managedLogout(): Promise<void> {
  const auth = await getClient();
  await auth.logout({
    logoutParams: { returnTo: window.location.origin },
  });
}
